import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type MessageBusService from 'garaje/services/message-bus';
import type SigmaUrlService from 'garaje/services/sigma-url';
import type StateService from 'garaje/services/state';
import { Workbook } from 'garaje/utils/sigma-workbooks';
import urlBuilder from 'garaje/utils/url-builder';

type Message = {
  query?: {
    location_id?: string;
    company_id?: string;
    flow_id?: string;
    start?: string;
    end?: string;
  };
  label?: string;
  event: 'showEntryExportModal' | 'showInviteExportModal' | 'closeExportModal';
};

export default class AnalyticsVisitorsController extends Controller {
  @service declare messageBus: MessageBusService;
  @service declare state: StateService;
  @service declare localStorage: LocalStorageService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare sigmaUrl: SigmaUrlService;

  @tracked showModal = false;
  @tracked exportIframeUrl = '';
  @tracked isError = false;

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  get isBoss(): boolean {
    return this.localStorage.getItem('is_boss') === 'true';
  }

  get legacyIframeUrl(): string {
    const companyId = this.state?.currentCompany?.id;
    const locationId = this.state?.currentLocation?.id;

    return urlBuilder.embeddedVisitorsLocationAnalyticsUrl(companyId, locationId);
  }

  fetchSigmaUrlTask = task({ drop: true }, async () => {
    const {
      currentCompany: { id: companyId },
      currentLocation: { id: locationId },
    } = this.state;
    try {
      const result = await this.sigmaUrl.getLocationSigmaUrl(companyId, locationId, Workbook.LOCATION_VISITORS);
      return result;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error loading dashboard');
      this.isError = true;
      // eslint-disable-next-line no-console
      console.error(e);
      return;
    }
  });

  get sigmaIframeUrl(): string | null | undefined {
    return this.fetchSigmaUrlTask.last?.value;
  }

  @action
  toggleShowModal(): void {
    this.showModal = !this.showModal;
  }

  @action
  handleMessage(message: Message): void {
    // eslint-disable-next-line no-console
    console.log('[VisitorsController] Received message:', message);
    const locationId = message.query?.location_id || '';
    const companyId = message.query?.company_id || '';
    const flowId = message.query?.flow_id || '';
    const start = message.query?.start || '';
    const end = message.query?.end || '';
    const label = message.label || '';

    // When feature flag is enabled, translate export events to new format
    if (this.featureFlags.isEnabled('analytics-improved-exports')) {
      if (message.event === 'showEntryExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'showModal',
          exportType: 'visitors',
          query: message.query,
          label: message.label,
        });
        return;
      } else if (message.event === 'showInviteExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'showModal',
          exportType: 'invites',
          query: message.query,
          label: message.label,
        });
        return;
      } else if (message.event === 'closeExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'closeModal',
        });
        return;
      }
    }

    // Old modal handling (when flag is disabled)
    if (message.event === 'showEntryExportModal') {
      this.exportIframeUrl = urlBuilder.embeddedEntryExportModalUrl(companyId, locationId, flowId, start, end, label);
      this.toggleShowModal();
    } else if (message.event === 'showInviteExportModal') {
      this.exportIframeUrl = urlBuilder.embeddedInviteExportModalUrl(companyId, locationId, flowId, start, end, label);
      this.toggleShowModal();
    } else if (message.event === 'closeExportModal') {
      this.toggleShowModal();
    }
  }
}
